import { Slot, Slottable } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, type LucideIcon } from 'lucide-react';
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from './lib/cn';

export const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-semibold leading-none transition-colors duration-fast ease-emphasis focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        /** Solid purple — "New message", "+ New", "Join". */
        primary: 'bg-primary text-primary-foreground shadow-brand hover:bg-primary-hover active:bg-primary-active',
        /** Outlined purple — "Meet now". */
        outline: 'border border-primary/60 bg-card text-primary hover:border-primary hover:bg-primary-subtle active:bg-brand-100',
        /** Neutral outline — secondary actions, "View Details". */
        'outline-neutral': 'border border-border bg-card text-secondary-foreground hover:bg-subtle active:bg-secondary',
        /** No chrome until hover — toolbar and row actions. */
        ghost: 'text-secondary-foreground hover:bg-subtle active:bg-secondary',
        /** Tinted, no border — selected pill tabs in the composer. */
        subtle: 'bg-primary-subtle text-primary-subtle-foreground hover:bg-brand-100 active:bg-brand-200',
        /** Neutral filled. */
        secondary: 'bg-secondary text-secondary-foreground hover:bg-muted active:bg-border',
        /** Solid red — "Leave", "Decline". */
        danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 active:bg-destructive/80',
        'danger-outline': 'border border-destructive/40 bg-card text-destructive hover:bg-destructive-subtle',
        /** Solid green — "Accept" on the incoming-call popup. */
        success: 'bg-success text-success-foreground hover:bg-success/90 active:bg-success/80',
        /** Text-only, purple — "See all", "5 replies". */
        link: 'h-auto rounded-xs p-0 font-medium text-primary underline-offset-2 hover:underline',
        /** Translucent light-on-dark — meeting stage and dark popups. */
        stage: 'bg-white/10 text-white backdrop-blur-sm hover:bg-white/20 active:bg-white/25',
      },
      size: {
        xs: 'h-7 px-2.5 text-sm',
        sm: 'h-8 px-3 text-base',
        md: 'h-9 px-3.5 text-md', //  36px — the app default
        lg: 'h-10 px-4 text-md', //   40px — "New message", "Meet now", "Leave"
        xl: 'h-11 px-5 text-lg', //   44px — popup Accept/Decline
      },
      fullWidth: { true: 'w-full', false: '' },
      /** Removes the right radius so a SplitButton chevron can dock onto it. */
      splitLeft: { true: 'rounded-r-none', false: '' },
      pill: { true: 'rounded-pill', false: '' },
    },
    defaultVariants: { variant: 'primary', size: 'md', fullWidth: false, splitLeft: false, pill: false },
  },
);

const ICON_SIZE = { xs: 14, sm: 14, md: 16, lg: 16, xl: 18 } as const;

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** lucide-react icon rendered before the label. */
  leftIcon?: LucideIcon;
  /** lucide-react icon rendered after the label. */
  rightIcon?: LucideIcon;
  /** Swaps the left icon for a spinner and disables interaction. */
  loading?: boolean;
  /** Render as the child element (e.g. a react-router <Link>). */
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    children, className, variant, size = 'md', fullWidth, splitLeft, pill,
    leftIcon: LeftIcon, rightIcon: RightIcon, loading = false,
    asChild = false, disabled, type = 'button', ...props
  },
  ref,
) {
  const Comp = asChild ? Slot : 'button';
  const iconSize = ICON_SIZE[size ?? 'md'];

  return (
    <Comp
      ref={ref}
      type={asChild ? undefined : type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(buttonVariants({ variant, size, fullWidth, splitLeft, pill }), className)}
      {...props}
    >
      {loading ? (
        <Loader2 size={iconSize} className="animate-spin" aria-hidden />
      ) : LeftIcon ? (
        <LeftIcon size={iconSize} strokeWidth={2.25} aria-hidden />
      ) : null}
      {/*
        `Slottable`, not a bare `{children}`.

        With `asChild`, `Comp` is Radix's `Slot`, which merges its props onto a
        SINGLE element child. The icon slots either side are children too — even
        when they render `null` — so Slot saw three and threw "Slot failed to
        slot onto its children", taking the whole route down with it. Every
        `<Button asChild>` in the product was a white screen waiting for the
        page that used it.

        Slottable names the merge target, so the icons keep rendering AROUND the
        link instead of having to be given up. Outside a Slot it is a fragment
        passthrough, so the plain <button> path is unchanged.
      */}
      <Slottable>{children}</Slottable>
      {RightIcon && !loading ? <RightIcon size={iconSize} strokeWidth={2.25} aria-hidden /> : null}
    </Comp>
  );
});
